/**
 * Mouse gesture → device action mapping (PLAN §3.5).
 *
 * Kept out of the component so the rules are testable on their own: this is
 * the part that decides whether a click was a tap, a hold, or a drag.
 */

export interface Point {
  x: number;
  y: number;
}

export type Gesture =
  | { kind: "tap"; x: number; y: number }
  | { kind: "long_press"; x: number; y: number; duration_ms: number }
  | { kind: "swipe"; x1: number; y1: number; x2: number; y2: number; duration_ms: number };

/** A press held at least this long is a long press rather than a tap. */
export const LONG_PRESS_MS = 500;
/** Movement below this fraction of the screen counts as a click, not a drag. */
export const DRAG_THRESHOLD = 0.02;
/** Floor for swipe duration so a flick still animates on the device. */
export const MIN_SWIPE_MS = 120;

export function classifyGesture(start: Point, end: Point, heldMs: number): Gesture {
  const travelled = Math.hypot(end.x - start.x, end.y - start.y);

  if (travelled > DRAG_THRESHOLD) {
    return {
      kind: "swipe",
      x1: start.x,
      y1: start.y,
      x2: end.x,
      y2: end.y,
      duration_ms: Math.max(heldMs, MIN_SWIPE_MS),
    };
  }
  if (heldMs >= LONG_PRESS_MS) {
    return { kind: "long_press", x: start.x, y: start.y, duration_ms: heldMs };
  }
  return { kind: "tap", x: start.x, y: start.y };
}

/** Merge wheel ticks that land in the same animation frame. */
export function accumulateWheel(
  pending: { x: number; deltaY: number } | null,
  x: number,
  deltaY: number,
): { x: number; deltaY: number } {
  return { x, deltaY: (pending?.deltaY ?? 0) + deltaY };
}

/** Wheel scrolling becomes a swipe in the opposite direction, under the cursor. */
export function wheelToSwipe(x: number, deltaY: number) {
  const down = deltaY > 0;
  return {
    x1: x,
    y1: down ? 0.7 : 0.3,
    x2: x,
    y2: down ? 0.3 : 0.7,
    duration_ms: 250,
  };
}

export type SwipeDirection = "up" | "down" | "left" | "right";

/** Full-screen swipe from the keypad, across the middle of the device. */
export function cardinalSwipe(direction: SwipeDirection, duration_ms = 280) {
  const mid = 0.5;
  const near = 0.78;
  const far = 0.22;
  switch (direction) {
    case "up":
      return { x1: mid, y1: near, x2: mid, y2: far, duration_ms };
    case "down":
      return { x1: mid, y1: far, x2: mid, y2: near, duration_ms };
    case "left":
      return { x1: near, y1: mid, x2: far, y2: mid, duration_ms };
    case "right":
      return { x1: far, y1: mid, x2: near, y2: mid, duration_ms };
  }
}

/** Clamp a client-space click into normalized 0–1 device coordinates. */
export function normalize(clientX: number, clientY: number, rect: DOMRect): Point {
  return {
    x: Math.min(Math.max((clientX - rect.left) / rect.width, 0), 1),
    y: Math.min(Math.max((clientY - rect.top) / rect.height, 0), 1),
  };
}

/** One recorded panel action, as `/scenarios/from-recording` expects it. */
export interface RecordedAction {
  kind: string;
  selector?: Record<string, unknown>;
  x?: number;
  y?: number;
  x2?: number;
  y2?: number;
  text?: string;
  key?: string;
  package?: string;
  url?: string;
  duration_ms?: number;
  label?: string;
  timeout_ms?: number;
}

/** The step a gesture records: selector-based when one resolved (PLAN §3.5). */
export function gestureToAction(
  gesture: Gesture,
  selector: Record<string, unknown> | undefined,
): RecordedAction {
  if (gesture.kind === "swipe") {
    return {
      kind: "swipe",
      x: gesture.x1,
      y: gesture.y1,
      x2: gesture.x2,
      y2: gesture.y2,
      duration_ms: gesture.duration_ms,
    };
  }
  const base: RecordedAction = { kind: gesture.kind };
  if (gesture.kind === "long_press") base.duration_ms = gesture.duration_ms;
  // Raw coordinates only when nothing resolved — the linter flags those.
  return selector ? { ...base, selector } : { ...base, x: gesture.x, y: gesture.y };
}
